// The latest broker calls of the current organisation: every call a worker made with a
// credential it never saw (GET /api/orgs/{org_id}/broker-calls).
import { CheckCircle, Prohibit, SealCheck, ShieldCheck } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { WidgetCard } from "../ui/Card";
import { type Column, DataTable } from "../ui/DataTable";
import { EmptyState } from "../ui/states";
import { Mono, Pill, type Tone } from "../ui/status";
import { shortId } from "../pages/format";
import { type BrokerCall, brokerCallsQuery } from "./api";
import { clock } from "./format";
import { QueryState } from "./QueryState";
import { formatNumber, type Messages, useT } from "../i18n";

const MS: Intl.NumberFormatOptions = { style: "unit", unit: "millisecond", unitDisplay: "short" };

type Known = keyof Messages["dashboard"]["calls"]["outcomes"];

const OUTCOME: Record<Known, { tone: Tone; icon: ReactNode }> = {
  allowed: { tone: "green", icon: <CheckCircle weight="fill" /> },
  approved: { tone: "primary", icon: <SealCheck weight="fill" /> },
  denied: { tone: "red", icon: <Prohibit weight="fill" /> },
};

const isKnown = (outcome: string): outcome is Known => outcome in OUTCOME;

function Outcome({ outcome }: { outcome: string }) {
  const t = useT().dashboard.calls;
  if (isKnown(outcome)) {
    const known = OUTCOME[outcome];
    return (
      <Pill tone={known.tone} icon={known.icon}>
        {t.outcomes[outcome]}
      </Pill>
    );
  }
  return <Pill tone={outcome === "error" || outcome === "failed" ? "red" : "gray"}>{outcome.replaceAll("_", " ")}</Pill>;
}

const columns = (t: Messages["dashboard"]["calls"]): Column<BrokerCall>[] => [
  { key: "at", header: t.time, cell: (c) => <Mono>{clock(c.at)}</Mono>, hideBelow: "sm" },
  { key: "action", header: t.action, cell: (c) => <span className="font-medium text-gray-900">{c.tool}.{c.action}</span> },
  { key: "resource", header: t.target, cell: (c) => <span className="text-gray-600">{c.resource || "—"}</span>, hideBelow: "md" },
  { key: "secret", header: t.secretUsed, cell: (c) => <Mono>{c.credential_name ?? "—"}</Mono>, hideBelow: "lg" },
  { key: "run", header: t.run, cell: (c) => <Mono>{c.run_id ? shortId(c.run_id) : "—"}</Mono>, hideBelow: "xl" },
  { key: "outcome", header: t.outcome, cell: (c) => <Outcome outcome={c.outcome} /> },
  { key: "ms", header: t.latency, cell: (c) => <span className="tabular text-gray-500">{c.latency_ms == null ? "—" : formatNumber(c.latency_ms, MS)}</span>, hideBelow: "sm", className: "text-end" },
];

function Body({ calls }: { calls: BrokerCall[] }) {
  const t = useT().dashboard.calls;
  if (calls.length === 0) {
    return (
      <EmptyState compact icon={<ShieldCheck weight="duotone" />} title={t.empty.title}>
        {t.noneBody}
      </EmptyState>
    );
  }
  return <DataTable label={t.tableLabel} columns={columns(t)} rows={calls} rowKey={(c) => `${c.at}-${c.run_id}-${c.tool}-${c.action}`} />;
}

export function BrokerCallsWidget({ orgId, className }: { orgId: string | undefined; className?: string }) {
  const { dashboard } = useT();
  const t = dashboard.calls;
  const query = useQuery({ ...brokerCallsQuery(orgId ?? ""), enabled: orgId !== undefined });
  return (
    <WidgetCard className={className} title={t.title} label={t.title} description={t.description}>
      {orgId === undefined ? (
        <EmptyState compact icon={<ShieldCheck weight="duotone" />} title={dashboard.noOrg} />
      ) : (
        <QueryState query={query} icon={<ShieldCheck weight="duotone" />} empty={t.empty}>
          {(calls) => <Body calls={calls} />}
        </QueryState>
      )}
    </WidgetCard>
  );
}
